import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, BookOpen, Award, MapPin } from "lucide-react";
import { MaskedLines, FadeUp, Overline } from "../components/Reveal";
import Seo from "../components/Seo";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { IMAGES, LINKS } from "../data/content";
import { getDailyLesson } from "../data/lessons";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-screen flex items-end overflow-hidden">
      <div className="absolute inset-0 bg-[#0A0A0A]" />
      <div className="absolute right-0 top-0 bottom-0 w-full lg:w-[52%]">
        <motion.div style={{ y: imgY }} className="relative h-full w-full">
          <img
            src={IMAGES.headshot}
            alt="Gurpreet Paul"
            data-testid="hero-portrait"
            className="h-full w-full object-cover object-top opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
        </motion.div>
      </div>
      <div className="absolute -left-40 top-1/3 w-[500px] h-[500px] rounded-full bg-brand/10 blur-[140px] pointer-events-none" />

      <motion.div style={{ y: textY }} className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full pb-28 pt-48">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Overline data-testid="hero-overline">Founder · CEO · Author</Overline>
        </motion.div>
        <MaskedLines
          lines={["Gurpreet", "Paul"]}
          className="font-display font-medium tracking-tighter leading-[0.9] text-[18vw] lg:text-[11rem] text-paper mt-6"
          delay={0.35}
        />
        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-white/60 text-base md:text-lg leading-relaxed font-body font-light"
        >
          Founder & CEO of ThirsTea USA. Author of <em className="text-paper not-italic font-normal">The Franchise Mindset</em>.
          Recognized by Marquis Who's Who in America — building brands, empowering entrepreneurs, creating legacies.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/journey"
            data-testid="hero-cta-journey"
            className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300"
          >
            Explore the Journey <ArrowRight size={15} />
          </Link>
          <a
            href={LINKS.amazon}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-book"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-[12px] uppercase tracking-[0.15em] text-paper hover:border-brand hover:text-brand transition-colors duration-300"
          >
            The Franchise Mindset <ArrowUpRight size={15} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex items-center gap-3 text-white/35 text-[11px] uppercase tracking-[0.25em] font-body"
        data-testid="hero-scroll-indicator"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block w-px h-10 bg-gradient-to-b from-brand to-transparent"
        />
      </motion.div>
    </section>
  );
};

const BENTO = [
  {
    span: "md:col-span-7",
    to: "/book",
    testid: "bento-book",
    overline: "The Book · 2026",
    title: "The Franchise Mindset",
    body: "How vision, persistence, and systems built a modern beverage brand. A true entrepreneur's journey — real lessons, lasting impact.",
    image: IMAGES.bookCover,
    imageClass: "absolute -right-6 -bottom-10 w-40 md:w-52 rotate-6 group-hover:rotate-3 transition-transform duration-700 red-glow",
  },
  {
    span: "md:col-span-5",
    to: "/awards",
    testid: "bento-award",
    overline: "Recognition · 2026",
    title: "Marquis Who's Who in America",
    body: "Honored for leadership in franchising, entrepreneurship and brand development worldwide in the beverage industry.",
  },
  {
    span: "md:col-span-5",
    to: "/journey",
    testid: "bento-journey",
    overline: "The Journey",
    title: "From India to the C-Suite",
    body: "PepsiCo to Coca-Cola to founder — five chapters of relentless execution across three decades and two continents.",
  },
  {
    span: "md:col-span-7",
    to: "/awards",
    testid: "bento-thirstea",
    overline: "ThirsTea USA · CEO",
    title: "#47 — Entrepreneur Top 150 New Franchises",
    body: "Leading one of America's fastest-growing boba tea franchise brands, with international expansion underway across India, Canada, Mexico and the Middle East.",
    image: IMAGES.desk,
    imageClass: "absolute right-0 top-0 h-full w-1/2 object-cover object-top opacity-40 group-hover:opacity-55 transition-opacity duration-700 [mask-image:linear-gradient(to_left,black,transparent)]",
  },
];

const Highlights = () => (
  <section data-testid="home-highlights" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32">
    <FadeUp>
      <Overline>Selected Chapters</Overline>
      <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-paper mt-6 max-w-2xl">
        A career built on <span className="italic text-brand">vision</span>, persistence & systems
      </h2>
    </FadeUp>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-16">
      {BENTO.map((card, i) => (
        <FadeUp key={card.testid} delay={i * 0.08} className={card.span}>
          <Link
            to={card.to}
            data-testid={card.testid}
            className="group relative block h-full min-h-[320px] overflow-hidden border border-white/10 bg-[#111113] p-8 md:p-10 hover:border-brand/50 transition-colors duration-500"
          >
            {card.image && (
              <img src={card.image} alt={card.title} className={card.imageClass} loading="lazy" />
            )}
            <div className="relative z-10 flex h-full flex-col justify-between gap-10 max-w-md">
              <p className="text-[11px] uppercase tracking-[0.25em] text-brand font-body">{card.overline}</p>
              <div>
                <h3 className="font-display font-medium tracking-tight text-2xl md:text-3xl text-paper leading-tight">
                  {card.title}
                </h3>
                <p className="mt-4 text-white/50 text-sm leading-relaxed font-body font-light">{card.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-white/40 group-hover:text-brand transition-colors duration-300 font-body">
                  Discover <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </span>
              </div>
            </div>
          </Link>
        </FadeUp>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="home-manifesto" className="border-t border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
      <FadeUp className="lg:col-span-4">
        <Overline>The Philosophy</Overline>
        <p className="mt-6 text-white/40 text-sm leading-relaxed font-body font-light max-w-xs">
          Attributed to extreme hard work, constant focus and persistent execution — moving beyond traditional work hours into continuous planning and implementation.
        </p>
      </FadeUp>
      <div className="lg:col-span-8">
        <FadeUp delay={0.1}>
          <blockquote className="font-display font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-paper">
            "The greatest risk in entrepreneurship is not failing.
            <span className="text-brand italic"> It is failing to start.</span>"
          </blockquote>
          <p className="mt-8 text-[12px] uppercase tracking-[0.25em] text-white/40 font-body">
            — The Franchise Mindset, 2026
          </p>
        </FadeUp>
      </div>
    </div>
  </section>
);

const DailyLesson = () => {
  const lesson = getDailyLesson();
  return (
    <section data-testid="home-daily-lesson" className="relative border-t border-white/10 bg-[#0D0D0F] overflow-hidden">
      <div className="absolute right-0 top-0 w-[400px] h-[400px] bg-brand/10 blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <FadeUp className="lg:col-span-4">
          <Overline>Lesson of the Day</Overline>
          <p className="mt-6 text-white/40 text-sm font-body font-light" data-testid="daily-lesson-date">{lesson.date}</p>
          <p className="mt-3 text-white/40 text-sm leading-relaxed font-body font-light max-w-xs">
            Lesson {String(lesson.n).padStart(2, "0")} of 50 — from the bonus section of The Franchise Mindset. A new one every day.
          </p>
          <Link
            to="/book"
            data-testid="daily-lesson-book-link"
            className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-white/40 hover:text-brand transition-colors duration-300 font-body"
          >
            Explore the book <ArrowUpRight size={14} />
          </Link>
        </FadeUp>
        <FadeUp delay={0.15} className="lg:col-span-8">
          <blockquote
            data-testid="daily-lesson-text"
            className="font-display font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-paper border-l-2 border-brand pl-8"
          >
            {lesson.title}
          </blockquote>
          <p className="mt-6 pl-8 text-white/50 text-base leading-relaxed font-body font-light max-w-2xl" data-testid="daily-lesson-body">
            {lesson.body}
          </p>
          <p className="mt-5 pl-8 text-brand/90 text-sm italic font-body font-light max-w-2xl" data-testid="daily-lesson-question">
            {lesson.question}
          </p>
        </FadeUp>
      </div>
    </section>
  );
};

const HomeCta = () => (
  <section data-testid="home-cta" className="relative border-t border-white/10 overflow-hidden">
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-brand/10 blur-[160px] pointer-events-none" />
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-32 text-left">
      <FadeUp>
        <Overline>Let's Build Something</Overline>
        <h2 className="font-display font-medium tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-paper mt-6 max-w-3xl">
          Franchising, speaking, or simply a conversation over tea.
        </h2>
        <Link
          to="/contact"
          data-testid="home-cta-contact"
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300"
        >
          Get in Touch <ArrowRight size={15} />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Gurpreet Paul — Founder & CEO of ThirsTea USA, Author"
        description="Gurpreet Paul is the Founder & CEO of ThirsTea USA, author of The Franchise Mindset, and a Marquis Who's Who in America 2026 honoree. Explore his journey, book, awards and franchise opportunities."
      />
      <Hero />
      <EditorialMarquee />
      <Highlights />
      <Manifesto />
      <DailyLesson />
      <HomeCta />
    </main>
  );
}
